/**
 * ExploreScreen - Redesigned
 * 
 * Browse books by category, search, and explore curated content
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  View,
  StyleSheet,
  ScrollView,
  TextInput,
  Pressable,
  ActivityIndicator,
  Animated,
  NativeScrollEvent,
  NativeSyntheticEvent,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import { Text, Theme, BookCard, Logo } from '../../components/ui';
import { BookService } from '../../services/bookService';
import { debounce } from '../../utils/debounce';
import { auth } from '../../config/firebase';
import { useNavigationLogging } from '../../hooks/useNavigationLogging';
import { useExploreSections } from '../../hooks/useExploreSections';
import { CATEGORY_BOOK_IDS } from '../../data/categoryBookIds';
import {
  BookCarouselSection,
  GuruRecommendationsSection,
  TopicChipsSection,
  CollectionGridSection,
} from '../shared/components';
import { FireBook } from '../../models/FirestoreModels';

export interface Category {
  id: string;
  name: string;
  icon: string;
}

// Categories ordered by number of books (most → least)
// Only includes categories with 6+ books
// Synced with CATEGORY_BOOK_IDS from data/categoryBookIds.ts
export const categories: Category[] = [
  { id: 'psychology', name: 'Psychology', icon: 'analytics-outline' },
  { id: 'parenting', name: 'Parenting', icon: 'heart-outline' },
  { id: 'health', name: 'Health', icon: 'fitness-outline' },
  { id: 'leadership', name: 'Leadership', icon: 'people-outline' },
  { id: 'business', name: 'Business', icon: 'briefcase-outline' },
  { id: 'science', name: 'Science', icon: 'flask-outline' },
  { id: 'philosophy', name: 'Philosophy', icon: 'school-outline' },
  { id: 'productivity', name: 'Productivity', icon: 'bulb-outline' },
  { id: 'spirituality', name: 'Spirituality', icon: 'sparkles-outline' },
  { id: 'management', name: 'Management', icon: 'git-network-outline' },
  { id: 'entrepreneurship', name: 'Entrepreneurship', icon: 'rocket-outline' },
  { id: 'finance', name: 'Finance', icon: 'trending-up-outline' },
  { id: 'lifestyle', name: 'Lifestyle', icon: 'leaf-outline' },
  { id: 'mentalhealth', name: 'Mental Health', icon: 'heart-circle-outline' },
  { id: 'relationships', name: 'Relationships', icon: 'heart-outline' },
  { id: 'communication', name: 'Communication', icon: 'chatbubbles-outline' },
  { id: 'marketing', name: 'Marketing', icon: 'megaphone-outline' },
  { id: 'money', name: 'Money', icon: 'cash-outline' },
  { id: 'creativity', name: 'Creativity', icon: 'color-palette-outline' },
  { id: 'biographymemoir', name: 'Biography & Memoir', icon: 'book-outline' },
  { id: 'personalfinance', name: 'Personal Finance', icon: 'wallet-outline' },
  { id: 'writing', name: 'Writing', icon: 'create-outline' },
  { id: 'wealth', name: 'Wealth', icon: 'diamond-outline' },
  { id: 'inspirational', name: 'Inspirational', icon: 'star-outline' },
  { id: 'investment', name: 'Investment', icon: 'stats-chart-outline' },
];

const ExploreScreen: React.FC = () => {
  useNavigationLogging('ExploreScreen');
  
  const navigation = useNavigation();
  const userId = auth.currentUser?.uid || null;
  const { data, loading } = useExploreSections(userId, 'en');
  
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<FireBook[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  
  // Animated value for search bar hide/show
  const searchBarHeight = useRef(new Animated.Value(0)).current;
  const progressAnim = useRef(new Animated.Value(0)).current;
  const lastScrollY = useRef(0);
  const scrollDirection = useRef<'up' | 'down'>('down');

  // Animation for the progress bar
  useEffect(() => {
    if (isSearching) {
      Animated.loop(
        Animated.sequence([
          Animated.timing(progressAnim, {
            toValue: 1,
            duration: 1500,
            useNativeDriver: false, // width/layout properties don't support native driver well
          }),
          Animated.timing(progressAnim, {
            toValue: 0,
            duration: 0,
            useNativeDriver: false,
          })
        ])
      ).start();
    } else {
      progressAnim.setValue(0);
    }
  }, [isSearching]);

  // Search: Run 1 second after user stops typing
  const runSearch = useCallback(
    debounce(async (text: string) => {
      if (!text || text.trim().length < 2) {
        setIsSearching(false);
        setSearchResults([]);
        return;
      }
      
      // isSearching is already true from useEffect
      try {
        const response = await BookService.vectorSearch(
          text,
          ['book'],
          'en',
          20,
          undefined,
          {
            audiences: [],
            audienceFilterMode: 'off',
          }
        );
        if (response.success && response.data) {
          setSearchResults(response.data.books);
        }
      } finally {
        setIsSearching(false);
      }
    }, 1000), // 1 second delay
    []
  );

  useEffect(() => {
    const text = searchQuery.trim();
    if (text.length > 0) {
      // Set searching state immediately when user types
      setIsSearching(true);
      runSearch(text);
    } else {
      setSearchResults([]);
      setIsSearching(false);
    }
  }, [searchQuery, runSearch]);

  const handleBookPress = (book: FireBook) => {
    const bookId = book.id.includes('_') ? book.id.split('_')[0] : book.id;
    // @ts-ignore - Navigation types
    navigation.navigate('BookDetail', {
      bookId,
      languageCode: book.languageCode || 'en',
    });
  };

  const handleTopicPress = (topic: Category) => {
    // @ts-ignore - Navigation types
    navigation.navigate('Category', {
      categoryId: topic.id,
      categoryName: topic.name,
    });
  };

  const handleScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
    const currentScrollY = event.nativeEvent.contentOffset.y;
    const delta = currentScrollY - lastScrollY.current;
    
    // Only animate if we've scrolled more than 5px
    if (Math.abs(delta) > 5) {
      if (delta > 0 && scrollDirection.current !== 'down') {
        // Scrolling down - hide search bar
        scrollDirection.current = 'down';
        Animated.timing(searchBarHeight, {
          toValue: -80, // Height of search bar
          duration: 200,
          useNativeDriver: true,
        }).start();
      } else if (delta < 0 && scrollDirection.current !== 'up') {
        // Scrolling up - show search bar
        scrollDirection.current = 'up';
        Animated.timing(searchBarHeight, {
          toValue: 0,
          duration: 200,
          useNativeDriver: true,
        }).start();
      }
      
      lastScrollY.current = currentScrollY;
    }
  };

  const handleGuruPress = (guru: any) => {
    // @ts-ignore - Navigation types
    navigation.navigate('GuruDetail', {
      guruId: guru.id,
      guruName: guru.name,
      guruImage: guru.imageUrl,
      bookIds: guru.bookIds,
    });
  };

  // Convert categories to topic chips format
  const topicChips = categories.map(cat => ({
    id: cat.id,
    name: cat.name,
    icon: cat.icon,
  }));

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={Theme.colors.primary} />
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.logoContainer}>
          <Logo size={22} />
          <Text style={styles.logoText}>mentorist</Text>
        </View>
      </View>

      {/* iOS-style Search Bar with hide on scroll */}
      <Animated.View 
        style={[
          styles.searchSection,
          {
            transform: [{ translateY: searchBarHeight }],
          },
        ]}
      >
        <View style={styles.searchBarContainer}>
          <View style={styles.searchBar}>
            <Ionicons
              name="search"
              size={18}
              color={Theme.colors.textSecondary}
              style={styles.searchIcon}
            />
            <TextInput
              style={styles.searchInput}
              placeholder="Search"
              placeholderTextColor={Theme.colors.textSecondary}
              value={searchQuery}
              onChangeText={setSearchQuery}
              returnKeyType="search"
              autoCapitalize="none"
              autoCorrect={false}
            />
            {searchQuery.length > 0 && (
              <Pressable 
                onPress={() => setSearchQuery('')}
                hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
              >
                <Ionicons
                  name="close-circle"
                  size={18}
                  color={Theme.colors.textSecondary}
                />
              </Pressable>
            )}
          </View>
        </View>
        
        {/* AI Processing Indicator */}
        {isSearching && (
          <View style={styles.aiProgressContainer}>
            <View style={styles.aiProgressContent}>
              <Ionicons name="sparkles" size={14} color={Theme.colors.primary} />
              <Text style={styles.aiProgressText}>
                Using AI to find the best result...
              </Text>
            </View>
            <View style={styles.progressBarBackground}>
              <Animated.View 
                style={[
                  styles.progressBarFill,
                  {
                    width: progressAnim.interpolate({
                      inputRange: [0, 1],
                      outputRange: ['0%', '100%']
                    }),
                    marginLeft: progressAnim.interpolate({
                        inputRange: [0, 0.5, 1],
                        outputRange: ['0%', '25%', '0%'] // slight movement effect
                    })
                  }
                ]} 
              />
            </View>
          </View>
        )}
      </Animated.View>

      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
        onScroll={handleScroll}
        scrollEventThrottle={16}
      >
        {/* Search Results */}
        {searchQuery.trim().length > 0 ? (
          <View style={styles.section}>
            <View style={styles.searchResultsHeader}>
              <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', flex: 1 }}>
                <Text variant="h3">
                  {isSearching 
                    ? 'Searching...' 
                    : `${searchResults.length} Result${searchResults.length !== 1 ? 's' : ''}`
                  }
                </Text>
              </View>
            </View>
            <View style={styles.searchResultsList}>
              {searchResults.map((book) => (
                <BookCard
                  key={book.id}
                  title={book.title}
                  author={book.author}
                  coverUrl={book.coverImageUrl || book.coverImageFullUrl}
                  insightsCount={book.insightIds?.length || 0}
                  variant="compact"
                  onPress={() => handleBookPress(book)}
                  style={styles.bookCardItem}
                />
              ))}
              {!isSearching && searchResults.length === 0 && (
                <View style={styles.noResults}>
                  <Ionicons name="search-outline" size={48} color={Theme.colors.textSecondary} />
                  <Text variant="body1" color="secondary" style={styles.noResultsText}>
                    No books found for "{searchQuery}"
                  </Text>
                  <Text variant="caption" color="secondary">
                    Try different keywords or browse by topic
                  </Text>
                </View>
              )}
            </View>
          </View>
        ) : (
          <>
            {/* Topics */}
            <TopicChipsSection
              title="I want to learn about"
              subtitle="Discover books by category and interest"
              topics={topicChips}
              onTopicPress={handleTopicPress}
            />

            {/* Guru Recommendations */}
            <GuruRecommendationsSection
              title="Recommends"
              subtitle="Curated by world-class mentors"
              recommendations={data.guruRecommendations}
              onGuruPress={handleGuruPress}
            />

            {/* Latest Summaries */}
            <BookCarouselSection
              title="Latest Summaries"
              subtitle="Fresh insights from newly added books"
              books={data.latestSummaries}
              icon="sparkles"
            />

            {/* Collections */}
            <CollectionGridSection
              title="Collections"
              subtitle="Curated book collections to help you grow"
              collections={data.collections}
            />

            {/* Heavy Hitters */}
            <BookCarouselSection
              title="Heavy-Hitting Books"
              subtitle="Timeless classics that shaped millions of lives"
              books={data.heavyHitters}
              icon="trophy"
            />

            {/* Popular Now */}
            <BookCarouselSection
              title="Popular Now"
              subtitle="See what's trending in the community"
              books={data.popularNow}
              icon="flame"
            />
          </>
        )}

        {/* Bottom spacing */}
        <View style={styles.bottomSpacing} />
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Theme.colors.background,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingTop: Theme.spacing.md,
    paddingBottom: Theme.spacing.xl,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  header: {
    paddingHorizontal: Theme.spacing.lg,
    paddingTop: Theme.spacing.md,
    paddingBottom: Theme.spacing.lg,
    backgroundColor: Theme.colors.surface,
  },
  logoContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Theme.spacing.xs,
  },
  logoText: {
    fontSize: 24,
    fontFamily: Theme.typography.fontFamily.semiBold,
    color: Theme.colors.textPrimary,
    lineHeight: 32,
  },
  searchSection: {
    backgroundColor: Theme.colors.background,
    paddingBottom: Theme.spacing.md,
    borderBottomWidth: 0.5,
    borderBottomColor: 'rgba(0, 0, 0, 0.1)',
    // Shadow for iOS style
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
  searchBarContainer: {
    paddingHorizontal: Theme.spacing.lg,
    paddingTop: Theme.spacing.md,
  },
  searchBar: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(142, 142, 147, 0.12)', // iOS gray background
    borderRadius: 10,
    paddingHorizontal: 10,
    paddingVertical: 8,
    height: 36, // iOS standard search bar height
  },
  searchIcon: {
    marginRight: 6,
  },
  searchInput: {
    flex: 1,
    fontSize: 17, // iOS standard font size
    color: Theme.colors.textPrimary,
    fontFamily: Theme.typography.fontFamily.regular,
    padding: 0, // Remove default padding
    lineHeight: 20, // Explicit line height for vertical centering
    textAlignVertical: 'center', // Android alignment
    includeFontPadding: false, // Remove extra Android padding
  },
  section: {
    marginTop: Theme.spacing.lg,
  },
  searchResultsHeader: {
    paddingHorizontal: Theme.spacing.lg,
    marginBottom: Theme.spacing.md,
  },
  searchResultsList: {
    paddingHorizontal: Theme.spacing.lg,
  },
  bookCardItem: {
    marginBottom: Theme.spacing.sm,
  },
  noResults: {
    alignItems: 'center',
    padding: Theme.spacing.xl,
    gap: Theme.spacing.sm,
  },
  noResultsText: {
    textAlign: 'center',
    marginTop: Theme.spacing.sm,
  },
  bottomSpacing: {
    height: Theme.spacing.xl,
  },
  aiProgressContainer: {
    paddingHorizontal: Theme.spacing.lg,
    marginTop: Theme.spacing.sm,
  },
  aiProgressContent: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 6,
    gap: 6,
  },
  aiProgressText: {
    fontSize: 13,
    color: Theme.colors.primary,
    fontFamily: Theme.typography.fontFamily.medium,
  },
  progressBarBackground: {
    height: 3,
    backgroundColor: 'rgba(0,0,0,0.05)',
    borderRadius: 2,
    overflow: 'hidden',
  },
  progressBarFill: {
    height: '100%',
    backgroundColor: Theme.colors.primary,
    borderRadius: 2,
  },
});

export default ExploreScreen;

